import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  EntityTypesSupportingSavedFilters,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import SavedFilter, {
  MapSavedFilter,
  RegularSavedFilter,
} from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import RoundButton from "@app/component/RoundButton";
import useView from "@app/component/view/utils/useView";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import convertMapFilterModelToFilterModel from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import useClearFilters from "@app/util/hook/useClearFilters";

import useStatus from "../utils/useStatus";

import styles from "./SavedFiltersButton.module.scss";

interface Props {
  implicitFilterFields?: IField[];
  savedFilter: SavedFilter;
  selectSavedFilter: typeof selectSavedFilter;
}

const SavedFilterButton: React.FC<Props> = ({
  implicitFilterFields,
  savedFilter,
  selectSavedFilter,
}) => {
  const {
    entityType,
    updateViewState,
    viewState: { filter, viewAs },
  } = useView();

  const resetAllFilters = useClearFilters(implicitFilterFields);

  const savedFilterFilter = useMemo(() => {
    return savedFilter.type === EntityType.PIN
      ? convertMapFilterModelToFilterModel(
          entityType as EntityTypesSupportedByMapsPage,
          (savedFilter as MapSavedFilter).filters
        )
      : (savedFilter as RegularSavedFilter).filters;
  }, [entityType, savedFilter]);

  const enabled = useStatus(savedFilterFilter, filter, implicitFilterFields, true);

  const handleClick = useCallback(() => {
    updateViewState({ selectedSavedFilterId: enabled ? undefined : savedFilter.id });
    selectSavedFilter({
      entityType:
        savedFilter.type === EntityType.PIN
          ? (entityType as EntityTypesSupportingSavedFilters)
          : savedFilter.type,
      savedFilter: enabled ? undefined : savedFilter,
    });
    if (enabled) {
      resetAllFilters();
    }
  }, [enabled, entityType, resetAllFilters, savedFilter, selectSavedFilter, updateViewState]);

  return (
    <RoundButton
      active={enabled && viewAs === savedFilter.viewAs}
      leftIcon={<FontAwesomeIcon className={styles.favoriteIcon} icon={faStar} size="1x" />}
      onClick={handleClick}
    >
      {savedFilter.name}
    </RoundButton>
  );
};

const mapDispatchToProps = {
  selectSavedFilter,
};

export default connect(null, mapDispatchToProps)(SavedFilterButton);
